import { useCallback, useMemo, useState } from 'react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert, Box, Button, Chip, Grid, MenuItem, TextField, Typography,
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import api from '../../services/api';
import DataTableCard from '../ui/DataTableCard';
import EnhancedDataGrid from '../ui/EnhancedDataGrid';
import GridCellTooltip from '../ui/GridCellTooltip';
import FoodOrderLifecycleActions from '../operations/FoodOrderLifecycleActions';
import { useFoodOrderAlerts } from '../../hooks/useFoodOrderAlerts';
import { formatMoney } from '../../utils/currency';
import { getDateRangeError } from '../../utils/dateRangeValidation';

function formatFoodLineCustomizations(item: {
  variants?: Array<{ name?: string }>;
  addons?: Array<{ name?: string; quantity?: number }>;
}) {
  const parts: string[] = [];
  for (const v of item.variants ?? []) {
    if (v?.name) parts.push(v.name);
  }
  for (const a of item.addons ?? []) {
    if (!a?.name) continue;
    const qty = a.quantity ?? 1;
    parts.push(qty > 1 ? `${a.name} x${qty}` : a.name);
  }
  return parts.join(' · ');
}

function formatOrderItems(row: { items?: Array<Record<string, unknown>> }) {
  const items = row.items ?? [];
  if (!items.length) return '—';
  return items.map((item) => {
    const name = String(item.name ?? 'Item');
    const qty = Number(item.quantity ?? 1);
    const custom = formatFoodLineCustomizations(item as {
      variants?: Array<{ name?: string }>;
      addons?: Array<{ name?: string; quantity?: number }>;
    });
    return custom ? `${name} x${qty} (${custom})` : `${name} x${qty}`;
  }).join('; ');
}

function personLabelWithPhone(v: unknown): string {
  if (!v) return '—';
  if (typeof v === 'string') return v;
  const o = v as { firstName?: string; lastName?: string; name?: string; phone?: string; email?: string };
  const name = [o.firstName, o.lastName].filter(Boolean).join(' ').trim() || o.name || '';
  const phone = String(o.phone || '').trim();
  const email = String(o.email || '').trim();
  if (name && phone) return `${name} · ${phone}`;
  if (name && email) return `${name} · ${email}`;
  return name || phone || email || '—';
}

function formatPaymentLabel(method: unknown): string {
  const m = String(method || '').trim();
  if (!m) return '—';
  const lower = m.toLowerCase();
  if (lower === 'cod' || lower === 'cash_on_delivery') return 'Cash';
  return lower.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDateTime(value: unknown): string {
  if (!value) return '—';
  const d = new Date(String(value));
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function timelineTimestamp(
  row: { timeline?: Array<{ status?: string; timestamp?: string }> },
  status: string,
): string | undefined {
  const events = row.timeline || [];
  for (let i = events.length - 1; i >= 0; i -= 1) {
    if (String(events[i]?.status || '').toLowerCase() === status && events[i]?.timestamp) {
      return String(events[i].timestamp);
    }
  }
  return undefined;
}

type HistoryEventRow = {
  status?: string;
  deliveredAt?: string;
  updatedAt?: string;
  cancelledAt?: string;
  timeline?: Array<{ status?: string; timestamp?: string }>;
};

function historyEventParts(row: HistoryEventRow): { primary: string; secondary?: string } {
  const status = String(row.status || '').toLowerCase();
  if (status === 'refunded') {
    const refundedAt = formatDateTime(timelineTimestamp(row, 'refunded') || row.updatedAt);
    const deliveredAt = formatDateTime(row.deliveredAt || timelineTimestamp(row, 'delivered'));
    return {
      primary: refundedAt === '—' ? 'Refunded Date/Time: —' : `Refunded Date/Time: ${refundedAt}`,
      secondary: deliveredAt === '—' ? undefined : `Delivered Date/Time: ${deliveredAt}`,
    };
  }
  if (status === 'cancelled') {
    const when = formatDateTime(
      row.cancelledAt || timelineTimestamp(row, 'cancelled') || row.updatedAt,
    );
    return { primary: when === '—' ? 'Cancelled Date/Time: —' : `Cancelled Date/Time: ${when}` };
  }
  const when = formatDateTime(row.deliveredAt || timelineTimestamp(row, 'delivered') || row.updatedAt);
  // Prefer Delivered wording — never label this column "Completed" for history rows.
  return { primary: when === '—' ? 'Delivered Date/Time: —' : `Delivered Date/Time: ${when}` };
}

function historyEventLabel(row: HistoryEventRow): string {
  const { primary, secondary } = historyEventParts(row);
  return secondary ? `${primary} · ${secondary}` : primary;
}

export type FoodOrderScope = 'live' | 'history' | 'scheduled';

const statusColors: Record<string, 'default' | 'primary' | 'success' | 'error' | 'warning' | 'info'> = {
  delivered: 'success', cancelled: 'error', refunded: 'error', pending: 'warning',
  confirmed: 'primary', preparing: 'info', ready: 'info', picked_up: 'info', delivering: 'info',
  completed: 'success',
};

const HISTORY_STATUSES = ['delivered', 'cancelled', 'refunded', 'completed'];
const PAYMENT_OPTIONS = ['cash', 'card', 'upi', 'wallet', 'corporate', 'online'];

type Props = {
  scope: FoodOrderScope;
  title: string;
  queryKeyPrefix?: string;
};

export default function FoodOrdersListPanel({ scope, title, queryKeyPrefix = 'food-orders' }: Props) {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [liveBanner, setLiveBanner] = useState<string | null>(null);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [status, setStatus] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [search, setSearch] = useState('');

  const dateRangeError = getDateRangeError(fromDate, toDate);
  const queryKey = [queryKeyPrefix, scope, page, pageSize, fromDate, toDate, status, paymentMethod, search];

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      const res = await api.get('/food/orders', {
        params: {
          scope,
          page: page + 1,
          limit: pageSize,
          ...(status ? { status } : {}),
          ...(fromDate ? { fromDate, from: fromDate } : {}),
          ...(toDate ? { toDate, to: toDate } : {}),
          ...(paymentMethod ? { paymentMethod } : {}),
          ...(search.trim() ? { search: search.trim(), q: search.trim() } : {}),
        },
      });
      return res.data;
    },
    enabled: !dateRangeError,
    refetchInterval: scope === 'live' ? 8000 : false,
    placeholderData: keepPreviousData,
  });

  const invalidate = useCallback(
    () => queryClient.invalidateQueries({ queryKey: [queryKeyPrefix] }),
    [queryClient, queryKeyPrefix],
  );

  useFoodOrderAlerts((payload) => {
    if (scope !== 'live') return;
    invalidate();
    if (payload.type === 'food_order_placed') {
      setLiveBanner(`New food order ${payload.orderNumber || ''} — accept now`.trim());
    }
  });

  const rows = dateRangeError ? [] : (data?.data || []);
  const total = data?.meta?.total ?? data?.pagination?.total ?? data?.total ?? rows.length;
  const hasFilters = Boolean(fromDate || toDate || status || paymentMethod || search.trim());

  const clearFilters = () => {
    setFromDate('');
    setToDate('');
    setStatus('');
    setPaymentMethod('');
    setSearch('');
    setPage(0);
  };

  const onFromDateChange = (value: string) => {
    setFromDate(value);
    if (value && toDate && toDate < value) setToDate(value);
    setPage(0);
  };

  const onToDateChange = (value: string) => {
    if (fromDate && value && value < fromDate) {
      setToDate(fromDate);
      return;
    }
    setToDate(value);
    setPage(0);
  };

  const columns: GridColDef[] = useMemo(() => [
    {
      field: 'orderNumber',
      headerName: 'Order No.',
      minWidth: 200,
      flex: 1,
      renderCell: (p) => <GridCellTooltip value={String(p.row.orderNumber || p.row._id || '—')} />,
    },
    {
      field: 'status',
      headerName: 'Status',
      minWidth: 130,
      width: 140,
      renderCell: (p) => (
        <Chip
          size="small"
          label={String(p.value || '').replace(/_/g, ' ')}
          color={statusColors[String(p.value).toLowerCase()] || 'default'}
          sx={{ fontWeight: 700, textTransform: 'none' }}
        />
      ),
    },
    {
      field: 'paymentMethod',
      headerName: 'Payment',
      minWidth: 120,
      width: 130,
      valueGetter: (_v, row) => formatPaymentLabel(row.paymentMethod),
      renderCell: (p) => <GridCellTooltip value={String(p.value ?? '—')} />,
    },
    {
      field: 'total',
      headerName: 'Amount',
      minWidth: 130,
      width: 140,
      valueGetter: (_v, row) => formatMoney(row.total ?? 0),
      renderCell: (p) => (
        <Typography variant="body2" fontWeight={800} sx={{ whiteSpace: 'nowrap' }}>
          {String(p.value ?? '—')}
        </Typography>
      ),
    },
    {
      field: 'customerId',
      headerName: 'Customer',
      flex: 1,
      minWidth: 190,
      valueGetter: (_v, row) => personLabelWithPhone(row.customerId),
      renderCell: (p) => <GridCellTooltip value={String(p.value ?? '—')} />,
    },
    {
      field: 'restaurantId',
      headerName: 'Restaurant',
      flex: 1.2,
      minWidth: 220,
      valueGetter: (_v, row) => row.restaurantId?.name || '—',
      renderCell: (p) => <GridCellTooltip value={String(p.value ?? '—')} />,
    },
    {
      field: 'partner',
      headerName: 'Delivery Partner',
      flex: 1.1,
      minWidth: 200,
      valueGetter: (_v, row) => {
        const label = personLabelWithPhone(row.deliveryPartnerId);
        return label === '—' ? 'Unassigned' : label;
      },
      renderCell: (p) => {
        const label = personLabelWithPhone(p.row.deliveryPartnerId);
        if (label === '—') {
          return <Chip size="small" label="Unassigned" variant="outlined" sx={{ fontWeight: 700 }} />;
        }
        return <GridCellTooltip value={label} />;
      },
    },
    ...(scope === 'scheduled' ? [{
      field: 'scheduledAt',
      headerName: 'Scheduled For',
      minWidth: 200,
      width: 210,
      valueGetter: (_v: unknown, row: { scheduledAt?: string }) => formatDateTime(row.scheduledAt),
      renderCell: (p: { value?: unknown }) => <GridCellTooltip value={String(p.value ?? '—')} />,
    } as GridColDef] : []),
    ...(scope === 'history' ? [{
      field: 'eventAt',
      headerName: 'Closed Date/Time',
      description: 'Cancelled Date/Time, Refunded Date/Time, or Delivered Date/Time — never labelled Completed',
      minWidth: 300,
      width: 320,
      flex: 1.2,
      valueGetter: (_v: unknown, row: HistoryEventRow) => historyEventLabel(row),
      renderCell: (p: { row: HistoryEventRow }) => {
        const { primary, secondary } = historyEventParts(p.row);
        return (
          <Box sx={{ py: 0.5, lineHeight: 1.25, overflow: 'hidden' }}>
            <Typography variant="body2" fontWeight={700} noWrap title={primary}>
              {primary}
            </Typography>
            {secondary && (
              <Typography variant="caption" color="text.secondary" fontWeight={600} noWrap title={secondary} display="block">
                {secondary}
              </Typography>
            )}
          </Box>
        );
      },
    } as GridColDef] : []),
    {
      field: 'items',
      headerName: 'Items',
      flex: 1,
      minWidth: 160,
      valueGetter: (_v, row) => formatOrderItems(row),
      renderCell: (p) => <GridCellTooltip value={formatOrderItems(p.row)} />,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: scope === 'history' ? 200 : 240,
      minWidth: scope === 'history' ? 180 : 180,
      sortable: false,
      renderCell: (p) => (
        <FoodOrderLifecycleActions order={p.row} onUpdated={invalidate} showAdminExtras />
      ),
    },
  ], [scope, invalidate]);

  return (
    <Box>
      {scope === 'history' && (
        <Box
          sx={{
            mb: 2,
            p: 2,
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 0.5 }}>
            Filter by date range
          </Typography>
          <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ mb: 1.5 }}>
            Use From / To to filter by completion, cancellation, or refund date. Also filter by status and payment type.
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="From date"
                value={fromDate}
                onChange={(e) => onFromDateChange(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: toDate || undefined }}
                slotProps={{ htmlInput: { max: toDate || undefined } }}
                error={!!dateRangeError}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="To date"
                key={`to-${fromDate || 'none'}`}
                value={toDate}
                onChange={(e) => onToDateChange(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: fromDate || undefined }}
                slotProps={{ htmlInput: { min: fromDate || undefined } }}
                error={!!dateRangeError}
                helperText={dateRangeError || undefined}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                select
                fullWidth
                size="small"
                label="Status"
                value={status}
                onChange={(e) => { setStatus(e.target.value); setPage(0); }}
              >
                <MenuItem value="">All</MenuItem>
                {HISTORY_STATUSES.map((s) => (
                  <MenuItem key={s} value={s}>{s.replace(/_/g, ' ')}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                select
                fullWidth
                size="small"
                label="Payment type"
                value={paymentMethod}
                onChange={(e) => { setPaymentMethod(e.target.value); setPage(0); }}
              >
                <MenuItem value="">All</MenuItem>
                {PAYMENT_OPTIONS.map((s) => (
                  <MenuItem key={s} value={s}>{formatPaymentLabel(s)}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Search orders"
                placeholder="Order #, address, city"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              />
            </Grid>
            <Grid item>
              <Button variant="outlined" color="error" size="small" onClick={clearFilters} disabled={!hasFilters}>
                Clear
              </Button>
            </Grid>
          </Grid>
          {dateRangeError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {dateRangeError}. Select an end date on or after the start date.
            </Alert>
          )}
        </Box>
      )}

      <DataTableCard title={title}>
        {scope === 'live' && liveBanner && (
          <Alert severity="warning" sx={{ mx: 2, mt: 2, mb: 0 }} onClose={() => setLiveBanner(null)}>
            {liveBanner}
          </Alert>
        )}

        <EnhancedDataGrid
          rows={rows}
          columns={columns}
          loading={isLoading}
          getRowId={(r) => r._id}
          paginationMode="server"
          rowCount={total}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(model) => {
            setPage(model.page);
            setPageSize(model.pageSize);
          }}
          pageSizeOptions={[10, 20, 50]}
          disableLocalSearch
          searchPlaceholder="Search orders"
          emptyTitle="No data found"
          emptySubtitle={
            dateRangeError
              ? 'Fix the date range to load order history'
              : hasFilters
                ? 'No orders match the selected filters'
                : 'No orders in this view yet'
          }
          columnVisibilityModel={{
            orderNumber: true,
            status: true,
            paymentMethod: true,
            total: true,
            customerId: true,
            restaurantId: true,
            partner: true,
            eventAt: true,
          }}
        />
      </DataTableCard>
    </Box>
  );
}
